/**
 * API Response Helpers
 * API 응답 데이터 정규화 헬퍼 함수
 *
 * @description
 * 다양한 API 응답 형식을 일관된 형식으로 변환하는 유틸리티
 * - 배열, 객체 내 배열 등 다양한 응답 형태 지원
 * - 타입 안전한 데이터 변환
 */

// ============================================================
// 상수 정의
// ============================================================

/** 배열 추출 시 검색할 기본 키 목록 */
const DEFAULT_ARRAY_KEYS = ['items', 'data', 'suppliers', 'riders', 'partners', 'delivery'] as const;

// ============================================================
// 타입 정의
// ============================================================

/**
 * 배열을 포함할 수 있는 API 응답 타입
 */
type ApiResponseWithArray<T> = T[] | {
    items?: T[];
    data?: T[];
    suppliers?: T[];
    riders?: T[];
    partners?: T[];
    [key: string]: T[] | unknown;
};

// ============================================================
// 배열 추출 함수
// ============================================================

/**
 * 다양한 응답 형식에서 배열 데이터 추출
 *
 * @description
 * API 응답이 배열이거나 객체 내에 배열을 포함하는 경우를 처리
 * 지원하는 키: items, data, suppliers, riders, partners, delivery
 *
 * @template T 배열 요소 타입
 * @param response API 응답 데이터
 * @param preferredKey 우선적으로 확인할 키 (선택)
 * @returns 추출된 배열 (없으면 빈 배열)
 *
 * @example
 * // 배열 응답
 * extractArrayFromResponse([{ id: 1 }]) // [{ id: 1 }]
 *
 * // 객체 내 배열
 * extractArrayFromResponse({ items: [{ id: 1 }] }) // [{ id: 1 }]
 *
 * // preferredKey 지정
 * extractArrayFromResponse({ suppliers: [{ id: 1 }] }, 'suppliers') // [{ id: 1 }]
 */
export function extractArrayFromResponse<T>(
    response: ApiResponseWithArray<T>,
    preferredKey?: string
): T[] {
    // 응답이 없는 경우
    if (!response) {
        return [];
    }

    // 응답이 배열인 경우
    if (Array.isArray(response)) {
        return response;
    }

    // 객체인 경우 키 순서대로 확인
    const keysToCheck = preferredKey
        ? [preferredKey, 'items', 'data', 'suppliers', 'riders', 'partners']
        : ['items', 'data', 'suppliers', 'riders', 'partners'];

    for (const key of keysToCheck) {
        const value = (response as Record<string, unknown>)[key];
        if (Array.isArray(value)) {
            return value as T[];
        }
    }

    return [];
}

/**
 * 날짜 문자열 YYYYMMDD -> YYYY-MM-DD 형식 변환
 *
 * @param ymd YYYYMMDD 형식 문자열
 * @returns YYYY-MM-DD 형식 문자열 (잘못된 형식이면 원본 반환)
 *
 * @example
 * formatYmdToDate('20240101') // '2024-01-01'
 * formatYmdToDate('2024-01-01') // '2024-01-01' (그대로)
 */
export function formatYmdToDate(ymd: string): string {
    if (!ymd || ymd.length !== 8) {
        return ymd || '';
    }
    return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
}

/**
 * 날짜 문자열 YYYY-MM-DD -> YYYYMMDD 형식 변환
 *
 * @param date YYYY-MM-DD 형식 문자열
 * @returns YYYYMMDD 형식 문자열
 *
 * @example
 * formatDateToYmd('2024-01-01') // '20240101'
 */
export function formatDateToYmd(date: string): string {
    return date ? date.replace(/-/g, '') : '';
}

/**
 * 값이 null/undefined인 경우 기본값 반환
 *
 * @template T 값 타입
 * @param value 원본 값
 * @param defaultValue 기본값
 * @returns 값 또는 기본값
 */
export function getValueOrDefault<T>(value: T | null | undefined, defaultValue: T): T {
    return value ?? defaultValue;
}

/**
 * 숫자 값 안전하게 변환
 *
 * @param value 변환할 값
 * @param defaultValue 기본값 (기본: 0)
 * @returns 숫자 값
 */
export function toSafeNumber(value: unknown, defaultValue = 0): number {
    if (value === null || value === undefined) {
        return defaultValue;
    }
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
}

/**
 * 문자열 값 안전하게 변환
 *
 * @param value 변환할 값
 * @param defaultValue 기본값 (기본: '')
 * @returns 문자열 값
 *
 * @example
 * toSafeString(123) // '123'
 * toSafeString(null) // ''
 */
export function toSafeString(value: unknown, defaultValue = ''): string {
    if (value === null || value === undefined) {
        return defaultValue;
    }
    return String(value);
}

// ============================================================
// 금액 변환 함수
// ============================================================

/**
 * 금액 문자열을 숫자로 안전하게 변환
 * 콤마 제거 후 숫자로 변환
 *
 * @param value 변환할 금액 값
 * @param defaultValue 기본값 (기본: 0)
 * @returns 숫자 값
 *
 * @example
 * toSafeAmount('1,000,000') // 1000000
 * toSafeAmount('1000') // 1000
 * toSafeAmount(null) // 0
 */
export function toSafeAmount(value: unknown, defaultValue = 0): number {
    if (value === null || value === undefined) {
        return defaultValue;
    }
    const strValue = String(value).replace(/,/g, '');
    const num = Number(strValue);
    return isNaN(num) ? defaultValue : num;
}

// ============================================================
// 데이터 정규화 헬퍼
// ============================================================

/**
 * 배열 데이터를 정규화하는 헬퍼 함수
 *
 * @template TRaw 원본 데이터 타입
 * @template TNormalized 정규화된 데이터 타입
 * @param response API 응답
 * @param normalizer 정규화 함수
 * @param preferredKey 배열 추출 시 우선 키
 * @returns 정규화된 데이터 배열
 *
 * @example
 * normalizeApiResponse(response, normalizeSupplier, 'suppliers')
 */
export function normalizeApiResponse<TRaw, TNormalized>(
    response: TRaw[] | Record<string, unknown>,
    normalizer: (item: TRaw) => TNormalized,
    preferredKey?: string
): TNormalized[] {
    const rawData = extractArrayFromResponse<TRaw>(response as TRaw[] | Record<string, TRaw[]>, preferredKey);
    return rawData.map(normalizer);
}

/**
 * 배열 데이터를 정규화하고 필터링하는 헬퍼 함수
 *
 * @template TRaw 원본 데이터 타입
 * @template TNormalized 정규화된 데이터 타입
 * @param response API 응답
 * @param normalizer 정규화 함수
 * @param filter 필터 함수
 * @param preferredKey 배열 추출 시 우선 키
 * @returns 정규화 및 필터링된 데이터 배열
 */
export function normalizeAndFilterApiResponse<TRaw, TNormalized>(
    response: TRaw[] | Record<string, unknown>,
    normalizer: (item: TRaw) => TNormalized,
    filter: (item: TNormalized) => boolean,
    preferredKey?: string
): TNormalized[] {
    const normalized = normalizeApiResponse(response, normalizer, preferredKey);
    return normalized.filter(filter);
}
